import { useEffect, useRef, useState } from "react";
import {
  CERTIFIED_LOGO_MAX_HEIGHT,
  CERTIFIED_LOGO_MAX_WIDTH,
} from "@shared/app-certification";
import { PAGE_X, PAGE_Y } from "../_shared/safeArea";
import { useTrackedScreen } from "../../hooks/useTrackedScreen";
import { Logo } from "../_shared/Logo";
import { RemoteHint, type RemoteHintItem } from "../_shared/RemoteHint";
import { ImageSlot } from "../_shared/ImageSlot";
import { SchemeSlot } from "../_shared/slots/SchemeSlot";
import CustomScreen from "../_shared/custom/CustomScreen";
import OffersEntry from "../_shared/offers/OffersEntry";
import VodScreen, { type VodScreenStyle } from "../_shared/vod/VodScreen";
import BuiltInPlayer from "../_shared/tv/BuiltInPlayer";
import GuideScreen from "../_shared/guide/GuideScreen";
import { useOfferSlot } from "../_shared/slots/offerSlot";
import { homeBgLayers } from "../_shared/bg";
import { useNow, formatDate, formatTime } from "../_shared/useNow";
import { useFocusGrid, useScreenFocus } from "../_shared/useFocusGrid";
import { useBackHome } from "../_shared/useBackHome";
import { useRadioNowPlaying } from "../_shared/useRadioNowPlaying";
import { useRadioPlayback } from "../_shared/useRadioPlayback";
import type { SchemeTokens } from "@shared/types/foyer";
import {
  SchemeProvider,
  useScheme,
  screenTitle,
  screenTitleOverride,
  visibleHomeSections,
  type SchemeContextValue,
  type ResolvedApp,
} from "../_shared/schemeContext";
import { launchApp } from "../../lib/launchApp";
import { openTvForThisScreen } from "../../lib/openTv";
import { bridge } from "../../lib/foyerBridge";
import { castSteps } from "../../lib/casting";
import type { SchemeProps } from "../registry";
import { atelierManifest } from "@shared/schemes/manifests";

// DEMO-1: TV + Screen Share, ported to Atelier/Mosaic/Spectrum from the
// launcher templates (`_shared/launcher/kit.tsx`). See SpectrumScheme.tsx for
// the full reasoning on why this is duplicated rather than imported.
//
// TV-6: the TV package name is NO LONGER duplicated here. Where the tile sends
// the screen - the vendor streaming app, or Foyer's own player when the premise
// has been switched to it - is decided in one place, `lib/openTv.ts`.

function tryOpenScreenShare(): boolean {
  const info = bridge.getCastingInfo();
  if (!info.supported || !info.launchable) return false;
  return bridge.launchCastReceiver();
}

/**
 * Atelier - calm editorial. Cream paper, deep navy, warm gold.
 * Cormorant Garamond + Inter Tight. Roman numerals, 0.5px hairlines,
 * gold mono kickers, italic em emphasis. Ported pixel-for-pixel from
 * the Claude Design `direction-atelier.jsx` handoff bundle.
 */

/** Reconstruct Atelier's palette object from resolved design tokens, so every
 *  `A.gold` reference below keeps working while honouring operator overrides.
 *  Defaults (== the original constants) come from the manifest. */
function atelierPalette(t: SchemeTokens) {
  const e = { ...atelierManifest.defaultTokens.extras, ...t.extras };
  return {
    paper: t.palette.bg,
    paperDk: e.paperDk,
    ink: t.palette.ink,
    ink2: e.ink2,
    taupe: t.palette.inkDim ?? "",
    rule: t.palette.rule ?? "",
    ruleSf: e.ruleSf,
    gold: t.palette.accent,
    goldDk: e.goldDk,
    serif: t.fonts.serif ?? "",
    sans: t.fonts.sans ?? "",
    mono: t.fonts.mono ?? "",
  };
}
function useA() {
  return atelierPalette(useScheme().tokens);
}

type Screen =
  | "home"
  | "apps"
  | "info"
  | "radio"
  | "hotel"
  | "custom"
  | "offers"
  // Foyer's own player (TV-3a). Distinct from the "TV" tile, which is still a
  // launch action - which application that action opens is a premise setting.
  // Foyer's own television — the shell the guide and the library live inside.
  // The TV TILE is still a launch action; which application it opens is a
  // premise setting (`tvPlayer`). This is what it opens when that setting
  // names Foyer.
  | "tv"
  | "vod"
  | "guide"
  | "cast";

const FOOTER_KEYS: RemoteHintItem[] = [
  { key: "◄ ►", label: "Move" },
  { key: "OK", label: "Select" },
  { key: "←", label: "Back" },
];

export default function AtelierScheme({ scheme }: SchemeProps) {
  const [screen, setScreen] = useTrackedScreen<Screen>("home");
  // Which offer the home banner asked for, if any (`POS-4`). Held beside the
  // screen rather than inside it: the Offers page is reached two ways now - the
  // menu tile, which opens the rail, and the banner, which opens one offer - and
  // only the caller knows which.
  const [offerId, setOfferId] = useState<string | null>(null);
  const scopeRef = useRef<HTMLDivElement>(null);
  useScreenFocus(scopeRef);
  // Every ordinary navigation clears the spotlight, so a tile that says
  // "Offers" always opens the shop rather than whatever the banner pointed at
  // three presses ago.
  const go = (s: Screen) => {
    setOfferId(null);
    setScreen(s);
  };
  useBackHome(() => go("home"));
  const A = atelierPalette(scheme.tokens);
  const { context } = scheme;
  const openOffer = (id: string) => {
    setOfferId(id);
    setScreen("offers");
  };
  // The dead "Hotel" screen goes somewhere real the moment the premise has a
  // shop: same tile, same name, an actual page behind it. With no live offers -
  // the switch off, the catalogue empty, everything out of hours - it is exactly
  // the screen it was, rather than an empty page.
  const hasOffers = (scheme.offers?.offers.length ?? 0) > 0;
  return (
    <SchemeProvider value={scheme}>
      <div
        ref={scopeRef}
        style={{
          position: "absolute",
          inset: 0,
          background: A.paper,
          color: A.ink,
          fontFamily: A.sans,
          outline: "none",
        }}
      >
        {screen === "home" && (
          <Home
            go={go}
            openOffer={openOffer}
            scopeRef={scopeRef}
            context={context}
          />
        )}
        {screen === "apps" && (
          <Apps go={go} scopeRef={scopeRef} context={context} />
        )}
        {screen === "info" && (
          <Info go={go} scopeRef={scopeRef} context={context} />
        )}
        {screen === "radio" && (
          <Radio go={go} scopeRef={scopeRef} context={context} />
        )}
        {screen === "custom" && (
          <AtelierCustom go={go} scopeRef={scopeRef} context={context} />
        )}
        {screen === "offers" && (
          <AtelierOffers
            go={go}
            offerId={offerId}
            scopeRef={scopeRef}
            context={context}
          />
        )}
        {screen === "tv" && (
          <BuiltInPlayer
            style={{ ...playerStyle(A), bg: A.paper }}
            onExit={() => go("home")}
            scopeRef={scopeRef}
          />
        )}
        {screen === "vod" && (
          <AtelierPlayer
            kind="vod"
            go={go}
            scopeRef={scopeRef}
            context={context}
          />
        )}
        {screen === "guide" && (
          <AtelierPlayer
            kind="guide"
            go={go}
            scopeRef={scopeRef}
            context={context}
          />
        )}
        {screen === "hotel" &&
          (hasOffers ? (
            <AtelierOffers go={go} scopeRef={scopeRef} context={context} />
          ) : (
            <HotelLaunch go={go} scopeRef={scopeRef} context={context} />
          ))}
        {screen === "cast" && (
          <Cast go={go} scopeRef={scopeRef} context={context} />
        )}
      </div>
    </SchemeProvider>
  );
}

// ── chrome ───────────────────────────────────────────────────────────────

function Header({ crumb }: { crumb?: string }) {
  const A = useA();
  const now = useNow();
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: `${PAGE_Y}px ${PAGE_X}px 14px`,
        borderBottom: `0.5px solid ${A.rule}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
        <Logo variant="dark" height={22} />
        {crumb && (
          <>
            <span style={{ color: A.rule, fontSize: 14 }}>·</span>
            <span
              style={{
                fontFamily: A.serif,
                fontSize: 18,
                fontStyle: "italic",
                color: A.taupe,
                letterSpacing: "0.01em",
              }}
            >
              {crumb}
            </span>
          </>
        )}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 22,
          color: A.taupe,
        }}
      >
        <span
          style={{
            fontFamily: A.mono,
            fontSize: 12,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          {formatDate(now)}
        </span>
        <span
          style={{
            fontFamily: A.mono,
            fontSize: 18,
            fontVariantNumeric: "tabular-nums",
            color: A.ink,
          }}
        >
          {formatTime(now)}
        </span>
      </div>
    </div>
  );
}

function Footer({
  items,
  context,
}: {
  items: RemoteHintItem[];
  context: SchemeContextValue;
}) {
  const A = useA();
  return (
    <div
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        padding: `14px ${PAGE_X}px ${PAGE_Y}px`,
        borderTop: `0.5px solid ${A.ruleSf}`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        color: A.taupe,
      }}
    >
      <RemoteHint items={items} style={{ color: A.taupe }} />
      <span
        style={{
          fontFamily: A.serif,
          fontStyle: "italic",
          fontSize: 13,
          color: A.taupe,
          letterSpacing: "0.01em",
        }}
      >
        {context.hotelName}
      </span>
    </div>
  );
}

// ── HOME ─────────────────────────────────────────────────────────────────

interface ScreenProps {
  go: (s: Screen) => void;
  scopeRef: React.RefObject<HTMLDivElement>;
  context: SchemeContextValue;
}

/**
 * The premise's own page. Atelier lends it the gold accent and its sans, and
 * nothing else: the operator's picture is the page (see CustomScreen).
 */
function AtelierCustom({ go, scopeRef }: ScreenProps) {
  const A = useA();
  return (
    <CustomScreen
      scopeRef={scopeRef}
      onBack={() => go("home")}
      style={{
        barBg: "rgba(26,29,61,0.45)",
        ink: A.paper,
        accent: A.gold,
        onAccent: A.ink,
        font: A.sans,
      }}
    />
  );
}

/**
 * Room offers, in Atelier's chrome: the shared renderer inside the template's
 * own header and footer, lent the gold accent and the Cormorant display face.
 * The page belongs to the launcher it was opened from; nothing about the rail
 * or the detail page is Atelier's business (see OffersScreen).
 */
function AtelierOffers({
  go,
  scopeRef,
  context,
  offerId,
}: ScreenProps & { offerId?: string | null }) {
  const A = useA();
  const scheme = useScheme();
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header crumb={screenTitle(scheme, "offers", "Offers")} />
      {/* Same inset every other Atelier sub-page uses; the 80px foot clears the
          absolutely-positioned Footer. */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          padding: `28px ${PAGE_X}px 80px`,
        }}
      >
        <OffersEntry
          offerId={offerId}
          scopeRef={scopeRef}
          onBack={() => go("home")}
          title={screenTitleOverride(scheme, "offers")}
          pad={{ x: 0, y: 0 }}
          hints={false}
          style={{
            bg: "transparent",
            card: A.paperDk,
            ink: A.ink,
            inkDim: A.taupe,
            rule: A.rule,
            accent: A.goldDk,
            onAccent: A.paper,
            titleFont: A.serif,
            font: A.sans,
            radius: 2,
            placeholder: A.paperDk,
          }}
        />
      </div>
      <Footer items={FOOTER_KEYS} context={context} />
    </div>
  );
}

/**
 * Foyer's own player, in Atelier's chrome (`TV-3a`): the shared renderers inside
 * the template's own header and footer, lent the gold accent and the Cormorant
 * display face. One component for both screens because Atelier's answer to both
 * is identical - the template supplies TOKENS, never layout - and two would only
 * be two places to forget an edit.
 */

/**
 * This template's palette as a `VodScreenStyle` — the one shape every player
 * surface speaks (`VodScreen`, `GuideScreen`, `TvShell`).
 *
 * Named once because it is now read twice: by the inset sub-pages, which sit
 * on the template's photograph and so want `bg: "transparent"`, and by
 * `BuiltInPlayer`, which REPLACES that photograph and overrides `bg` with the
 * template's own surface.
 */
function playerStyle(A: ReturnType<typeof useA>): VodScreenStyle {
  return {
    bg: "transparent",
    card: A.paperDk,
    ink: A.ink,
    inkDim: A.taupe,
    rule: A.rule,
    accent: A.goldDk,
    onAccent: A.paper,
    titleFont: A.serif,
    font: A.sans,
    radius: 2,
    placeholder: A.paperDk,
  };
}

function AtelierPlayer({
  kind,
  go,
  scopeRef,
  context,
}: ScreenProps & { kind: "vod" | "guide" }) {
  const A = useA();
  const scheme = useScheme();
  const fallback = kind === "vod" ? "Films & series" : "TV guide";
  // Identical field sets, so one literal serves both (see `VodScreenStyle`).
  const style = playerStyle(A);
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header crumb={screenTitle(scheme, kind, fallback)} />
      {/* Same inset every other Atelier sub-page uses; the 80px foot clears the
          absolutely-positioned Footer. */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          padding: `28px ${PAGE_X}px 80px`,
        }}
      >
        {kind === "vod" ? (
          <VodScreen
            scopeRef={scopeRef}
            onBack={() => go("home")}
            title={screenTitleOverride(scheme, "vod")}
            pad={{ x: 0, y: 0 }}
            hints={false}
            style={style}
          />
        ) : (
          <GuideScreen
            scopeRef={scopeRef}
            onBack={() => go("home")}
            title={screenTitleOverride(scheme, "guide")}
            pad={{ x: 0, y: 0 }}
            hints={false}
            style={style}
          />
        )}
      </div>
      <Footer items={FOOTER_KEYS} context={context} />
    </div>
  );
}

/**
 * The Atelier home tile's words for each destination.
 *
 * Indexed by screen id and NOT optional-chained on purpose: an id that reaches
 * `visibleHomeSections` without an entry here throws, which is the loud version
 * of a tile that opens nothing. Any screen a manifest lists in `homeOrder.items`
 * must therefore land here in the same commit. Mosaic and Spectrum keep the same
 * contract in their own `DEST_DEF` tables.
 */
const HOME_DEST: Record<string, { label: string; kicker: string }> = {
  apps: { label: "Streaming", kicker: "12 apps · Netflix, Prime, Spotify…" },
  info: { label: "Information", kicker: "Wi-Fi, dining, the floor plan" },
  radio: { label: "Radio", kicker: "12 stations · DAB+ & online" },
  // The premise's own page. `label` is the fallback only - the name comes from
  // the `customTitle` knob, read at render (the property named this screen).
  custom: { label: "Custom", kicker: "A page of our own" },
  // Room offers. Same rule: the name comes from `offersTitle`.
  offers: { label: "Offers", kicker: "Order in · book the spa" },
  // Foyer's own player. Same rule again: the names come from `vodTitle` /
  // `guideTitle`, read at render.
  // Kickers kept SHORT: the tile strip is a fixed-height `1fr` row, so every
  // extra destination narrows every column (see HOME_ROMAN).
  vod: { label: "Films & series", kicker: "Watch here, now" },
  guide: { label: "TV guide", kicker: "What is on tonight" },
};
/**
 * Numerals for the fixed TV/Hotel tiles plus every optional destination.
 *
 * DEMO-1 added Screen Share and took the ceiling to VIII; TV-3a's `vod` +
 * `guide` take it to X. Carries two spare on purpose: `HOME_ROMAN[i] ?? ""` is a
 * SILENT fallback, so a list one short renders a tile with a blank figure and
 * nothing anywhere says why.
 *
 * ⚠️ The tile STRIP is the real ceiling, not this list. It is a fixed-height
 * `repeat(tiles.length, 1fr)` row, so every screen an operator adds narrows all
 * of them; past about ten the serif labels start wrapping to three lines. If a
 * template ever needs more destinations than that, the strip has to wrap or
 * paginate - lengthening this array alone will not save it.
 */
const HOME_ROMAN = [
  "I",
  "II",
  "III",
  "IV",
  "V",
  "VI",
  "VII",
  "VIII",
  "IX",
  "X",
  "XI",
  "XII",
];

function Home({
  go,
  openOffer,
  scopeRef,
  context,
}: ScreenProps & { openOffer: (offerId: string) => void }) {
  const A = useA();
  const scheme = useScheme();
  const now = useNow();
  // A live offer in the promo slot is a second ROW of the home grid, sitting
  // where it draws: above the tile strip, over the hero. Only when there is one
  // - an empty (or dangling, or expired) slot must not leave a d-pad cell that
  // moves the ring onto nothing.
  const banner = useOfferSlot("homePromo", now);
  // Premise background image: the operator's hero photo fills the welcome slot
  // (honouring content.background.mode - `color` falls back to the placeholder).
  const hero = scheme.content.images.hero;
  const bgL = homeBgLayers(scheme.content.background?.mode);
  // TV is the fixed FIRST tile - always present, never toggleable, same rule
  // the launcher templates and Care follow (DEMO-1). Screen Share follows it
  // when its toggle is on. Hotel keeps its own established slot right after;
  // the rest come from the visible, ordered home sections (honouring the
  // toggle/order knobs + content-driven hiding). The roman numeral renumbers
  // by position, and the grid re-flows to the count.
  const showCast = scheme.knobs.has("showCast")
    ? scheme.knobs.toggle("showCast")
    : true;
  const tiles = [
    {
      id: "tv",
      label: "TV",
      kicker: "Live channels · the remote",
      go: "tv" as Screen,
    },
    ...(showCast
      ? [
          {
            id: "cast",
            label: "Screen Share",
            kicker: "Mirror a phone or laptop",
            go: "cast" as Screen,
          },
        ]
      : []),
    {
      id: "hotel",
      label: "Hotel",
      kicker: "Concierge · Service",
      go: "hotel" as Screen,
    },
    ...visibleHomeSections(scheme).map((id) => ({
      id,
      label: screenTitle(scheme, id, HOME_DEST[id].label),
      kicker: HOME_DEST[id].kicker,
      go: id as Screen,
    })),
  ].map((t, i) => ({ ...t, figure: HOME_ROMAN[i] ?? "" }));
  // **Row 0 is the TILES and row 1 the banner**, even though the banner draws
  // above them. With two rows and wrap, Up and Down are the same toggle, so the
  // numbering costs nothing - and it is what keeps the ring off the banner at
  // mount. `useFocusGrid` reads `initial` ONCE, and the offers feed arrives a
  // beat after the home screen does (`useRoomOffers` polls; only the design
  // preview has it on the first render). Numbered the other way round, a ring
  // that started on row 0 = "the tiles" would silently become row 0 = "the
  // banner" the moment the feed landed, and the guest's first OK would open an
  // offer they never chose. An offer nobody asked for may interrupt the eye,
  // never the remote (`docs/room-offers.md` §12).
  // TV is a launch action, not a sub-page (DEMO-1) - same rule the launcher
  // templates follow. Screen Share tries the device's own receiver first and
  // only falls back to the instructions page when there is nothing to open.
  // Shared by the d-pad (`onEnter`) and the mouse (`onClick`) paths below.
  const pressDest = (id: string) => {
    if (id === "tv") {
      // `openBuiltin` is what gives the premise switch teeth: a premise on
      // `tvPlayer: "foyer"` opens our own player, one on "norigin" (the
      // default) still hands the screen to the vendor app.
      //
      // 🚨 It opens the PLAYER, not the guide. Dropping a guest straight into
      // the channel list is the behaviour `1fd8e91` replaced — "a channel
      // list, not a television: landing in one leaf of a structure with no way
      // to reach the others reads as a broken app rather than a smaller one".
      // Only the launcher kit was migrated then; this template kept the
      // superseded call, so one button meant two things depending on which
      // design a premise had chosen.
      openTvForThisScreen({
        openBuiltin: () => go("tv"),
        // The premise's choice from the config this render is using — the CMS
        // preview has one and no pairing binding to read a cache with.
        tvPlayer: scheme.context.tvPlayer,
      });
      return;
    }
    if (id === "cast") {
      if (tryOpenScreenShare()) return;
      go("cast");
      return;
    }
    go(id as Screen);
  };
  const [focus, setFocus] = useFocusGrid({
    rows: banner ? 2 : 1,
    cols: tiles.length,
    initial: { r: 0, c: 0 },
    scopeRef,
    onEnter: (f) =>
      banner && f.r === 1
        ? openOffer(banner.offer.id)
        : pressDest(tiles[f.c].id),
    wrap: true,
  });
  // The other direction: the offer's window closes (or its row is deleted) under
  // a guest sitting on the banner, and row 1 stops existing. Pull the ring back
  // rather than leave the home screen with no ring on anything.
  useEffect(() => {
    if (!banner && focus.r !== 0) setFocus({ r: 0, c: focus.c });
  }, [banner, focus.r, focus.c, setFocus]);
  const onTiles = focus.r === 0;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header />
      <div
        style={{
          position: "relative",
          flex: "1 1 auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          padding: `0 ${PAGE_X}px`,
        }}
      >
        <ImageSlot
          kind="welcome image · landscape"
          tone="light"
          src={bgL.photo ? hero : undefined}
          style={{
            position: "absolute",
            top: 16,
            left: 36,
            right: 36,
            bottom: 200,
            borderRadius: 2,
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(to bottom, transparent 50%, ${A.paper} 96%)`,
            }}
          />
        </ImageSlot>
        <div
          style={{
            position: "relative",
            paddingBottom: 14,
            marginBottom: 18,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 14,
              marginBottom: 6,
            }}
          >
            <span
              style={{
                fontFamily: A.mono,
                fontSize: 11,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: A.gold,
              }}
            >
              № 01 · Welcome
            </span>
            <span
              style={{ flex: 1, height: 1, background: A.rule }}
              aria-hidden
            />
            <span
              style={{
                fontFamily: A.serif,
                fontSize: 14,
                fontStyle: "italic",
                color: A.taupe,
              }}
            >
              Est. 1908
              {context.city ? ` · ${context.city}` : ""}
            </span>
          </div>
          <h1
            style={{
              margin: 0,
              fontFamily: A.serif,
              fontWeight: 400,
              fontSize: 72,
              lineHeight: 0.94,
              letterSpacing: "-0.01em",
              color: A.ink,
            }}
          >
            Welcome{" "}
            <em style={{ fontStyle: "italic", color: A.goldDk }}>home</em>,
            <br />
            <span
              style={{
                fontSize: 38,
                color: A.taupe,
                fontStyle: "italic",
              }}
            >
              to {context.hotelName}.
            </span>
          </h1>
        </div>
        {/* Operator promo slot - pre-positioned editorial callout, top-right
            over the hero. Template owns placement + palette; operator fills it. */}
        <SchemeSlot
          slotId="homePromo"
          focused={!!banner && focus.r === 1}
          onPress={openOffer}
          style={{
            bg: A.paperDk,
            ink: A.ink,
            inkDim: A.taupe,
            accent: A.gold,
            rule: A.rule,
            font: A.sans,
            kickerFont: A.mono,
            radius: 2,
          }}
          containerStyle={{
            position: "absolute",
            top: 28,
            // 36, not 0: the containing block is the padding BOX, so `right: 0`
            // put the card hard against the panel edge - inside the overscan a
            // television eats, and with its focus ring half off-screen now that
            // the card can hold one. This lines it up with the hero photo and
            // every other Atelier inset instead.
            right: 36,
            width: 460,
            zIndex: 3,
          }}
        />
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${tiles.length}, 1fr)`,
          gap: 0,
          borderTop: `0.5px solid ${A.rule}`,
          padding: `0 ${PAGE_X}px`,
          height: 156,
          marginBottom: 50,
        }}
      >
        {tiles.map((t, i) => {
          const active = onTiles && focus.c === i;
          const isFirst = i === 0;
          const isLast = i === tiles.length - 1;
          return (
            <button
              key={t.id}
              onMouseEnter={() => setFocus({ r: 0, c: i })}
              onClick={() => pressDest(t.id)}
              style={{
                appearance: "none",
                border: 0,
                textAlign: "left",
                padding: `22px ${isLast ? 0 : 22}px 22px ${isFirst ? 0 : 22}px`,
                position: "relative",
                cursor: "pointer",
                color: "inherit",
                fontFamily: "inherit",
                borderRight: isLast ? "none" : `0.5px solid ${A.rule}`,
                background: active ? "rgba(184,153,92,0.08)" : "transparent",
                transition: "background 200ms ease",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: -1,
                  left: isFirst ? 0 : 22,
                  right: isLast ? 0 : 22,
                  height: 2,
                  background: A.gold,
                  transform: active ? "scaleX(1)" : "scaleX(0)",
                  transformOrigin: "left",
                  transition: "transform 280ms cubic-bezier(0.2,0.8,0.2,1)",
                }}
              />
              <div
                style={{
                  fontFamily: A.serif,
                  fontSize: 14,
                  fontStyle: "italic",
                  color: active ? A.goldDk : A.taupe,
                  marginBottom: 8,
                }}
              >
                {t.figure}
              </div>
              <div
                style={{
                  fontFamily: A.serif,
                  fontSize: 32,
                  lineHeight: 1,
                  color: A.ink,
                  marginBottom: 8,
                  letterSpacing: "-0.01em",
                }}
              >
                {t.label}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: A.taupe,
                  lineHeight: 1.4,
                }}
              >
                {t.kicker}
              </div>
            </button>
          );
        })}
      </div>
      <Footer items={FOOTER_KEYS.slice(0, 2)} context={context} />
    </div>
  );
}

// ── APPS ─────────────────────────────────────────────────────────────────

function Apps({ go, scopeRef, context }: ScreenProps) {
  const A = useA();
  const { content } = useScheme();
  const cols = 4;
  const rows = 3;
  const items = [
    { id: "hotel", name: "Hotel", isHotel: true as const },
    ...content.apps
      .slice(0, 11)
      .map((a) => ({ ...a, isHotel: false as const })),
  ];
  const [focus, setFocus] = useFocusGrid({
    rows,
    cols,
    scopeRef,
    onEnter: (f) => {
      const it = items[f.r * cols + f.c];
      if (!it) return;
      if (it.isHotel) go("hotel");
      else launchApp(it);
    },
    onBack: () => go("home"),
  });
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header crumb="Streaming & apps" />
      <div
        style={{
          flex: 1,
          padding: `28px ${PAGE_X}px 80px`,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            marginBottom: 18,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontFamily: A.serif,
              fontWeight: 400,
              fontSize: 44,
              letterSpacing: "-0.01em",
            }}
          >
            Apps{" "}
            <em style={{ color: A.taupe, fontSize: 24 }}>
              · twelve in residence
            </em>
          </h2>
          <div
            style={{
              fontFamily: A.mono,
              fontSize: 11,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: A.gold,
            }}
          >
            № 02
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            // Rows are sized by the cards' own 16:9, not stretched to fill the
            // page: `repeat(rows, 1fr)` plus a fixed height would override the
            // ratio and quietly hand back a letterboxed tile.
            gap: 16,
            alignContent: "start",
            maxHeight: "calc(100% - 80px)",
          }}
        >
          {items.map((it, i) => {
            const r = Math.floor(i / cols);
            const c = i % cols;
            const active = focus.r === r && focus.c === c;
            return (
              <AppCard
                key={it.id}
                app={it}
                active={active}
                onMouseEnter={() => setFocus({ r, c })}
                onClick={() => (it.isHotel ? go("hotel") : launchApp(it))}
              />
            );
          })}
        </div>
      </div>
      <Footer items={FOOTER_KEYS} context={context} />
    </div>
  );
}

interface AppCardProps {
  app:
    | { id: string; name: string; isHotel: true }
    | (ResolvedApp & { isHotel: false });
  active: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}

function AppCard({ app, active, onMouseEnter, onClick }: AppCardProps) {
  const A = useA();
  const isHotel = app.isHotel;
  return (
    <button
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      style={{
        appearance: "none",
        border: 0,
        padding: 0,
        cursor: "pointer",
        textAlign: "left",
        background: isHotel ? A.ink : app.color || "#222",
        color: isHotel ? A.paper : app.text || "#fff",
        position: "relative",
        // 16:9 - the frame the certification describes (NFLX-5), and the shape
        // landscape app artwork wants anyway.
        aspectRatio: "16 / 9",
        // border-box, or the ratio governs the CONTENT box and the padding is
        // added on top - which renders 1.65:1, not 16:9. Measured, not assumed.
        boxSizing: "border-box",
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: active
          ? `0 0 0 2px ${A.gold}, 0 18px 32px -10px rgba(26,29,61,0.35)`
          : `0 0 0 0.5px ${A.rule}`,
        transform: active ? "translateY(-2px)" : "none",
        transition:
          "transform 220ms cubic-bezier(0.2,0.7,0.2,1), box-shadow 220ms",
        fontFamily: "inherit",
      }}
    >
      <div
        style={{
          padding: "14px 16px 12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontFamily: A.mono,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            opacity: 0.7,
          }}
        >
          {isHotel ? "⌂ House" : "App"}
        </span>
        <span style={{ fontSize: 11, fontFamily: A.mono, opacity: 0.5 }}>
          {String(app.id).slice(0, 3).toUpperCase()}
        </span>
      </div>
      <div
        style={{
          position: "absolute",
          left: 16,
          right: 16,
          bottom: 14,
          fontFamily: isHotel ? A.serif : "inherit",
          fontSize: isHotel ? 30 : 22,
          fontWeight: isHotel ? 400 : 600,
          letterSpacing: isHotel ? "-0.01em" : "-0.02em",
        }}
      >
        {isHotel ? (
          "Hotel"
        ) : (app as ResolvedApp).logoUrl ? (
          <img
            src={(app as ResolvedApp).logoUrl}
            alt={app.name}
            style={{
              maxHeight: (app as ResolvedApp).certified
                ? CERTIFIED_LOGO_MAX_HEIGHT
                : 46,
              maxWidth: (app as ResolvedApp).certified
                ? CERTIFIED_LOGO_MAX_WIDTH
                : "85%",
              objectFit: "contain",
              display: "block",
            }}
          />
        ) : (
          (app as ResolvedApp).wordmark
        )}
      </div>
      {isHotel && (
        <div
          style={{
            position: "absolute",
            right: 14,
            bottom: 12,
            fontFamily: A.serif,
            fontSize: 13,
            fontStyle: "italic",
            color: A.gold,
          }}
        >
          Concierge · Order · Spa
        </div>
      )}
    </button>
  );
}

// ── INFO ─────────────────────────────────────────────────────────────────

function Info({ go, scopeRef, context }: ScreenProps) {
  const A = useA();
  const [tab, setTab] = useState<"directory" | "welcome">("directory");
  const tabs: ("directory" | "welcome")[] = ["directory", "welcome"];
  const [focus, setFocus] = useFocusGrid({
    rows: 1,
    cols: 2,
    scopeRef,
    onEnter: (f) => setTab(tabs[f.c]),
    onBack: () => go("home"),
  });
  useEffect(() => {
    setFocus({ r: 0, c: tabs.indexOf(tab) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header crumb="Information" />
      <div
        style={{
          flex: 1,
          padding: `28px ${PAGE_X}px 80px`,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontFamily: A.serif,
              fontWeight: 400,
              fontSize: 44,
              letterSpacing: "-0.01em",
            }}
          >
            <em style={{ fontStyle: "italic", color: A.taupe }}>The</em> house
            guide
          </h2>
          <div
            style={{
              display: "flex",
              gap: 4,
              padding: 4,
              background: A.paperDk,
              borderRadius: 999,
            }}
          >
            {tabs.map((t, i) => {
              const active = tab === t;
              const focused = focus.c === i;
              return (
                <button
                  key={t}
                  onMouseEnter={() => setFocus({ r: 0, c: i })}
                  onClick={() => setTab(t)}
                  style={{
                    appearance: "none",
                    border: 0,
                    padding: "8px 18px",
                    background: active ? A.ink : "transparent",
                    color: active ? A.paper : A.ink,
                    borderRadius: 999,
                    cursor: "pointer",
                    fontFamily: A.serif,
                    fontSize: 16,
                    fontStyle: "italic",
                    boxShadow:
                      focused && !active ? `inset 0 0 0 1px ${A.gold}` : "none",
                    transition: "all 180ms",
                  }}
                >
                  {t === "directory" ? "Directory" : "Welcome floor"}
                </button>
              );
            })}
          </div>
        </div>
        {tab === "directory" && <Directory />}
        {tab === "welcome" && <WelcomeFloor />}
      </div>
      <Footer items={FOOTER_KEYS} context={context} />
    </div>
  );
}

function Directory() {
  const A = useA();
  const { content } = useScheme();
  return (
    <div
      style={{
        flex: 1,
        columnCount: 2,
        columnGap: 56,
        columnFill: "balance",
        overflow: "hidden",
      }}
    >
      {content.infoSections.map((sec, idx) => (
        <div key={sec.id} style={{ breakInside: "avoid", marginBottom: 24 }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 12,
              borderBottom: `0.5px solid ${A.rule}`,
              paddingBottom: 6,
              marginBottom: 10,
            }}
          >
            <h3
              style={{
                margin: 0,
                fontFamily: A.serif,
                fontWeight: 400,
                fontSize: 24,
                letterSpacing: "-0.01em",
              }}
            >
              {sec.section}
            </h3>
            <span style={{ flex: 1 }} />
            <span
              style={{
                fontFamily: A.mono,
                fontSize: 10,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: A.gold,
              }}
            >
              {String(idx + 1).padStart(2, "0")}
            </span>
          </div>
          {sec.items.map((it) => (
            <div
              key={it.label}
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 16,
                padding: "5px 0",
                fontSize: 14,
                lineHeight: 1.4,
              }}
            >
              <span style={{ color: A.taupe }}>{it.label}</span>
              <span style={{ color: A.ink, textAlign: "right" }}>
                {it.value}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function WelcomeFloor() {
  const A = useA();
  const rooms = [
    { label: "Lobby", x: 16, y: 22 },
    { label: "Restaurant Lumen", x: 38, y: 28 },
    { label: "Lobby Bar", x: 60, y: 32 },
    { label: "Library", x: 80, y: 32 },
    { label: "Reception", x: 16, y: 65 },
    { label: "Grand Hall", x: 38, y: 70 },
    { label: "Spa · Pool", x: 72, y: 78 },
  ];
  const floors = [
    { f: "Roof", t: "Rooftop garden · pavilion bar" },
    { f: "6", t: "Suites 601-612" },
    { f: "2-5", t: "Guest rooms" },
    { f: "G", t: "Lobby · dining · library" },
    { f: "B1", t: "Spa · pool · gym" },
  ];
  return (
    <div
      style={{
        flex: 1,
        display: "grid",
        gridTemplateColumns: "1fr 280px",
        gap: 28,
        minHeight: 0,
      }}
    >
      <div
        style={{
          position: "relative",
          background: A.paperDk,
          borderRadius: 4,
          boxShadow: `inset 0 0 0 0.5px ${A.rule}`,
          overflow: "hidden",
        }}
      >
        <svg
          viewBox="0 0 1000 560"
          preserveAspectRatio="none"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
          }}
        >
          <defs>
            <pattern
              id="atelier-grid"
              width="40"
              height="40"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M40 0L0 0 0 40"
                fill="none"
                stroke="rgba(26,29,61,0.06)"
                strokeWidth="0.5"
              />
            </pattern>
          </defs>
          <rect width="1000" height="560" fill="url(#atelier-grid)" />
          <rect
            x="80"
            y="60"
            width="840"
            height="440"
            fill="none"
            stroke={A.ink}
            strokeWidth="2"
          />
          <line
            x1="80"
            y1="240"
            x2="520"
            y2="240"
            stroke={A.ink}
            strokeWidth="1.5"
          />
          <line
            x1="520"
            y1="60"
            x2="520"
            y2="500"
            stroke={A.ink}
            strokeWidth="1.5"
          />
          <line
            x1="520"
            y1="320"
            x2="920"
            y2="320"
            stroke={A.ink}
            strokeWidth="1.5"
          />
          <line
            x1="240"
            y1="240"
            x2="240"
            y2="500"
            stroke={A.ink}
            strokeWidth="1"
            opacity="0.5"
          />
          <line
            x1="720"
            y1="60"
            x2="720"
            y2="320"
            stroke={A.ink}
            strokeWidth="1"
            opacity="0.5"
          />
          <path
            d="M180 60 a60 60 0 0 1 60 60"
            fill="none"
            stroke={A.ink}
            strokeWidth="1"
            opacity="0.6"
          />
          <path
            d="M520 280 a40 40 0 0 1 40 40"
            fill="none"
            stroke={A.ink}
            strokeWidth="1"
            opacity="0.6"
          />
          <rect
            x="82"
            y="62"
            width="158"
            height="178"
            fill="rgba(184,153,92,0.10)"
          />
          <rect
            x="240"
            y="62"
            width="280"
            height="178"
            fill="rgba(184,153,92,0.05)"
          />
          <rect
            x="520"
            y="62"
            width="200"
            height="258"
            fill="rgba(26,29,61,0.05)"
          />
          <rect
            x="720"
            y="62"
            width="200"
            height="258"
            fill="rgba(26,29,61,0.04)"
          />
          <rect
            x="82"
            y="240"
            width="158"
            height="260"
            fill="rgba(184,153,92,0.04)"
          />
          <rect
            x="240"
            y="240"
            width="280"
            height="260"
            fill="rgba(184,153,92,0.07)"
          />
          <rect
            x="520"
            y="320"
            width="400"
            height="180"
            fill="rgba(26,29,61,0.03)"
          />
        </svg>
        {rooms.map((r) => (
          <div
            key={r.label}
            style={{
              position: "absolute",
              left: `${r.x}%`,
              top: `${r.y}%`,
              transform: "translate(-50%,-50%)",
              fontFamily: A.serif,
              fontStyle: "italic",
              fontSize: 16,
              color: A.ink,
              textShadow: "0 1px 0 rgba(245,239,228,0.8)",
              letterSpacing: "0.01em",
            }}
          >
            {r.label}
          </div>
        ))}
        <div
          style={{
            position: "absolute",
            bottom: 14,
            right: 14,
            display: "flex",
            alignItems: "center",
            gap: 14,
            fontFamily: A.mono,
            fontSize: 10,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: A.taupe,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 30, height: 1, background: A.ink }} />
            <span>10 m</span>
          </div>
          <div style={{ position: "relative", width: 22, height: 22 }}>
            <div
              style={{
                position: "absolute",
                top: 0,
                left: "50%",
                width: 1,
                height: "100%",
                background: A.ink,
                transform: "translateX(-50%)",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: -3,
                left: "50%",
                transform: "translateX(-50%)",
                fontSize: 9,
              }}
            >
              N
            </div>
          </div>
        </div>
        <div
          style={{
            position: "absolute",
            top: 14,
            left: 14,
            fontFamily: A.serif,
            fontStyle: "italic",
            fontSize: 12,
            color: A.taupe,
          }}
        >
          Ground floor · plan A-01 · 1:200
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 14,
          paddingTop: 4,
        }}
      >
        <div
          style={{
            fontFamily: A.mono,
            fontSize: 10,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: A.gold,
          }}
        >
          The house · floors
        </div>
        {floors.map((row) => (
          <div
            key={row.f}
            style={{
              display: "flex",
              gap: 14,
              alignItems: "baseline",
              borderBottom: `0.5px solid ${A.ruleSf}`,
              paddingBottom: 8,
            }}
          >
            <div
              style={{
                fontFamily: A.serif,
                fontSize: 26,
                color: A.ink,
                width: 40,
              }}
            >
              {row.f}
            </div>
            <div
              style={{
                fontSize: 13,
                color: A.taupe,
                lineHeight: 1.4,
              }}
            >
              {row.t}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── RADIO ────────────────────────────────────────────────────────────────

function Radio({ go, scopeRef, context }: ScreenProps) {
  const A = useA();
  const stations = useScheme().content.stations;
  // Audio playback (start on OK / stop on leave); `playingId` null until OK.
  const { playingId, toggle, audioRef } = useRadioPlayback(stations);
  const [focus, setFocus] = useFocusGrid({
    rows: stations.length,
    cols: 1,
    scopeRef,
    onEnter: (f) => toggle(stations[f.r]?.id),
    onBack: () => go("home"),
  });
  // The hero PREVIEWS the focused (hovered) station; OK starts playback.
  const station = stations[focus.r] ?? stations[0];
  const isPlaying = !!station && station.id === playingId;
  const np = useRadioNowPlaying(stations.map((s) => s.metadataId));
  const live = station?.metadataId ? np[station.metadataId] : undefined;
  const program = live?.program ?? station?.nowPlaying;
  const song = live?.song;
  const rowRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    rowRef.current?.scrollIntoView({ block: "nearest" });
  }, [focus.r]);
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <audio ref={audioRef} preload="none" />
      <Header crumb="Radio" />
      <div
        style={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: "1.1fr 1fr",
          gap: 0,
          padding: "0 0 64px",
          minHeight: 0,
        }}
      >
        <div
          style={{
            padding: "32px 36px",
            display: "flex",
            flexDirection: "column",
            borderRight: `0.5px solid ${A.rule}`,
          }}
        >
          <div
            style={{
              fontFamily: A.mono,
              fontSize: 11,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: A.gold,
              marginBottom: 6,
            }}
          >
            {isPlaying ? "On air" : "Preview"}
          </div>
          <h2
            style={{
              margin: 0,
              fontFamily: A.serif,
              fontWeight: 400,
              fontSize: 64,
              lineHeight: 0.95,
              letterSpacing: "-0.02em",
            }}
          >
            {station.name}
          </h2>
          <div
            style={{
              fontFamily: A.serif,
              fontStyle: "italic",
              fontSize: 22,
              color: A.taupe,
              marginTop: 8,
            }}
          >
            {station.tag} · {station.freq}
          </div>
          {/* vinyl */}
          <div
            style={{
              margin: "32px 0 24px",
              position: "relative",
              width: 220,
              height: 220,
              borderRadius: "50%",
              background: `radial-gradient(circle at 50% 50%, ${A.gold} 0 6%, ${A.ink} 6.5% 100%)`,
              boxShadow:
                "inset 0 0 0 1px rgba(255,255,255,0.05), 0 8px 30px -10px rgba(26,29,61,0.4)",
            }}
          >
            {[40, 60, 80, 100].map((r) => (
              <div
                key={r}
                style={{
                  position: "absolute",
                  inset: 0,
                  margin: "auto",
                  width: `${r}%`,
                  height: `${r}%`,
                  borderRadius: "50%",
                  boxShadow: "inset 0 0 0 0.5px rgba(184,153,92,0.18)",
                }}
              />
            ))}
            <div
              style={{
                position: "absolute",
                inset: 0,
                margin: "auto",
                width: 14,
                height: 14,
                borderRadius: "50%",
                background: A.paper,
                boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.2)",
              }}
            />
          </div>
          <div>
            <div
              style={{
                fontSize: 12,
                color: A.taupe,
                fontFamily: A.mono,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              {isPlaying ? "Now playing" : "Preview"}
            </div>
            <div
              style={{
                fontFamily: A.serif,
                fontSize: 26,
                color: A.ink,
                fontStyle: "italic",
                marginTop: 4,
              }}
            >
              {program}
            </div>
            {song && (
              <div
                style={{
                  fontSize: 14,
                  color: A.taupe,
                  fontFamily: A.mono,
                  marginTop: 4,
                }}
              >
                ♪ {song}
              </div>
            )}
          </div>
          <div
            style={{
              marginTop: "auto",
              display: "flex",
              alignItems: "flex-end",
              gap: 4,
              height: 36,
            }}
          >
            {Array.from({ length: 64 }).map((_, i) => {
              const h =
                8 + (Math.sin(i * 0.4) * 0.5 + 0.5) * 26 + (i % 5) * 1.5;
              return (
                <div
                  key={i}
                  style={{
                    width: 3,
                    height: h,
                    background: i < 18 ? A.gold : A.rule,
                    borderRadius: 2,
                  }}
                />
              );
            })}
          </div>
        </div>
        <div
          style={{
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              padding: "20px 30px 12px",
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              borderBottom: `0.5px solid ${A.rule}`,
            }}
          >
            <span
              style={{
                fontFamily: A.serif,
                fontStyle: "italic",
                fontSize: 22,
              }}
            >
              Stations
            </span>
            <span
              style={{
                fontFamily: A.mono,
                fontSize: 10,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: A.taupe,
              }}
            >
              {stations.length} curated
            </span>
          </div>
          <div
            className="foyer-scheme-scroll"
            style={{ flex: 1, overflow: "auto", padding: "4px 0" }}
          >
            {stations.map((s, i) => {
              const active = focus.r === i;
              const rowPlaying = playingId === s.id;
              return (
                <button
                  key={s.id}
                  ref={active ? rowRef : undefined}
                  onMouseEnter={() => setFocus({ r: i, c: 0 })}
                  onClick={() => toggle(s.id)}
                  style={{
                    appearance: "none",
                    border: 0,
                    width: "100%",
                    textAlign: "left",
                    padding: "12px 30px",
                    display: "grid",
                    gridTemplateColumns: "36px 1fr auto",
                    gap: 16,
                    alignItems: "center",
                    background: active
                      ? "rgba(184,153,92,0.10)"
                      : "transparent",
                    borderLeft: active
                      ? `2px solid ${A.gold}`
                      : "2px solid transparent",
                    cursor: "pointer",
                    color: "inherit",
                    fontFamily: "inherit",
                    transition: "background 160ms",
                  }}
                >
                  <span
                    style={{
                      fontFamily: A.mono,
                      fontSize: 11,
                      color: A.taupe,
                      letterSpacing: "0.06em",
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <span
                        style={{
                          fontFamily: A.serif,
                          fontSize: 22,
                          color: A.ink,
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {s.name}
                      </span>
                      {rowPlaying && (
                        <span
                          style={{
                            fontSize: 9,
                            fontFamily: A.mono,
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                            color: A.paper,
                            background: A.gold,
                            padding: "2px 6px",
                            borderRadius: 999,
                          }}
                        >
                          On air
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: A.taupe,
                        marginTop: 1,
                      }}
                    >
                      {s.tag}
                      {s.freq ? ` · ${s.freq}` : ""}
                    </div>
                  </span>
                  <span
                    style={{
                      fontFamily: A.mono,
                      fontSize: 11,
                      color: A.taupe,
                    }}
                  >
                    {s.listeners}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <Footer items={FOOTER_KEYS} context={context} />
    </div>
  );
}

// ── HOTEL LAUNCH ─────────────────────────────────────────────────────────

function HotelLaunch({
  go,
  scopeRef,
  context,
}: {
  go: (s: Screen) => void;
  scopeRef: React.RefObject<HTMLDivElement>;
  context: SchemeContextValue;
}) {
  const A = useA();
  // The hotel screen's only action is to return home. Wire OK and the Back key
  // to it and keep the Back control focused so the remote can always escape.
  useFocusGrid({
    rows: 1,
    cols: 1,
    scopeRef,
    onEnter: () => go("home"),
    onBack: () => go("home"),
  });
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: A.ink,
        color: A.paper,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          padding: "20px 36px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Logo variant="light" height={22} />
        <button
          onClick={() => go("home")}
          style={{
            appearance: "none",
            border: `1px solid ${A.gold}`,
            background: "rgba(184,153,92,0.12)",
            color: A.paper,
            padding: "6px 14px",
            borderRadius: 999,
            fontFamily: "inherit",
            fontSize: 12,
            cursor: "pointer",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            boxShadow: "0 0 0 3px rgba(184,153,92,0.30)",
          }}
        >
          ← Back
        </button>
      </div>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          padding: "0 80px",
        }}
      >
        <div
          style={{
            fontFamily: A.mono,
            fontSize: 11,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: A.gold,
            marginBottom: 16,
          }}
        >
          Concierge · Now opening
        </div>
        <h1
          style={{
            margin: 0,
            fontFamily: A.serif,
            fontWeight: 400,
            fontSize: 72,
            lineHeight: 1,
            letterSpacing: "-0.02em",
          }}
        >
          {context.hotelName.split(" ")[0] ?? "The Hotel"}{" "}
          <em style={{ color: A.gold }}>at your service</em>
        </h1>
        <p
          style={{
            marginTop: 18,
            fontSize: 16,
            color: "rgba(245,239,228,0.7)",
            maxWidth: 540,
            lineHeight: 1.55,
          }}
        >
          Order to the room, book the spa, request housekeeping, settle the
          bill, or extend the stay - everything the front desk can do, on the
          screen.
        </p>
        <div
          style={{
            marginTop: 36,
            display: "flex",
            gap: 0,
            fontFamily: A.serif,
            fontSize: 16,
            fontStyle: "italic",
            color: "rgba(245,239,228,0.6)",
          }}
        >
          {[
            "In-room dining",
            "Housekeeping",
            "Spa",
            "Late check-out",
            "Bill",
          ].map((s, i) => (
            <span
              key={s}
              style={{
                padding: "0 18px",
                borderLeft:
                  i > 0 ? "0.5px solid rgba(245,239,228,0.2)" : "none",
              }}
            >
              {s}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Screen Share (DEMO-1). Reached only as the FALLBACK: the "Screen Share"
 * tile tries `tryOpenScreenShare()` first and only calls `go("cast")` when
 * there is nothing to open (a set-top box entered from its own source list,
 * the CMS preview). Pure instruction, never the casting UI itself - see
 * SpectrumScheme.tsx's `Cast` for the fuller reasoning.
 */
function Cast({
  go,
  scopeRef,
  context,
}: {
  go: (s: Screen) => void;
  scopeRef: React.RefObject<HTMLDivElement>;
  context: SchemeContextValue;
}) {
  const A = useA();
  useFocusGrid({
    rows: 1,
    cols: 1,
    scopeRef,
    onEnter: () => go("home"),
    onBack: () => go("home"),
  });
  const room = context.roomId ? `Room ${context.roomId}` : "Guest TV";
  const canCast = bridge.capabilities().guestCasting;
  const castInfo = bridge.getCastingInfo();
  const castName = castInfo.deviceName?.trim() || room;
  const steps = castSteps({
    info: canCast ? castInfo : null,
    deviceName: castName,
  });
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header crumb="Screen Share" />
      <div style={{ padding: "28px 36px 0" }}>
        <h1
          style={{
            margin: 0,
            fontFamily: A.serif,
            fontWeight: 400,
            fontSize: 44,
            letterSpacing: "-0.01em",
          }}
        >
          Share your{" "}
          <em style={{ fontStyle: "italic", color: A.goldDk }}>screen</em>.
        </h1>
        <p style={{ margin: "8px 0 24px", fontSize: 14, color: A.taupe }}>
          Mirror your phone, tablet or laptop onto this television.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 14,
          }}
        >
          {steps.map((s) => (
            <div
              key={s.n}
              style={{
                background: A.paperDk,
                border: `0.5px solid ${A.rule}`,
                borderRadius: 2,
                padding: "22px 20px",
              }}
            >
              <div
                style={{
                  fontFamily: A.serif,
                  fontSize: 28,
                  color: A.gold,
                }}
              >
                {s.n}
              </div>
              <div
                style={{
                  fontFamily: A.serif,
                  fontSize: 20,
                  marginTop: 4,
                  color: A.ink,
                }}
              >
                {s.title}
              </div>
              <div style={{ fontSize: 13.5, color: A.taupe, marginTop: 6 }}>
                {s.detail}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
